import { type IconProps } from '@tabler/icons-react'
import { type ElementType, type ReactNode } from 'react'
import { NavLink } from 'react-router'

import { AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraTooltip } from '../AstraTooltip/AstraTooltip'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { useSidenavContext } from './AstraSideNavContext'

type LinkItem = {
    type: 'link'
    to: string
    onNavigate?: () => void
}

type Action = {
    type: 'action'
    onClick: () => void
}

type SidebarItemContentProps = {
    icon: ElementType<IconProps>
    label: string
    rightSlot?: ReactNode
    rightSlotClassName?: string
}

export const SidebarItemContent = ({ icon: Icon, label, rightSlot, rightSlotClassName, isOpen }: SidebarItemContentProps & { isOpen: boolean }) =>
    isOpen ? (
        <>
            <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                <AstraIcon icon={Icon} size="large" color="text-default" />

                <AstraParagraph className="whitespace-nowrap">{label}</AstraParagraph>
            </div>

            {rightSlot ? <div className={astraTwMerge('ml-auto', rightSlotClassName)}>{rightSlot}</div> : null}
        </>
    ) : (
        <AstraIcon icon={Icon} size="large" color="text-default" />
    )

export type SidebarItemProps = SidebarItemContentProps & (LinkItem | Action)
export const sideNavItemBaseClassName = 'w-[stretch] mx-1 flex h-8 cursor-pointer items-center justify-between rounded-lg px-1 transition'

export const AstraSideNavItem = ({ icon: Icon, label, rightSlot, rightSlotClassName, ...props }: SidebarItemProps) => {
    const isOpen = useSidenavContext()
    const isLink = props.type === 'link'

    return (
        <AstraTooltip tooltipContent={label} side="right">
            {isLink ? (
                <NavLink
                    data-test={`${props.to.trim().replace('/', '')}-link`}
                    to={props.to}
                    onClick={props.onNavigate}
                    className={({ isActive }) =>
                        astraTwMerge(
                            sideNavItemBaseClassName,
                            isActive
                                ? 'bg-surface-base drop-shadow-sm'
                                : 'hover:bg-surface-backdrop focus:bg-surface-base/50 focus:drop-shadow-sm focus:transition-none'
                        )
                    }
                >
                    <SidebarItemContent icon={Icon} label={label} rightSlot={rightSlot} rightSlotClassName={rightSlotClassName} isOpen={isOpen} />
                </NavLink>
            ) : (
                <button className={astraTwMerge(sideNavItemBaseClassName, 'hover:bg-surface-backdrop')} onClick={props.onClick} data-test={`${label}-action`}>
                    <SidebarItemContent icon={Icon} label={label} rightSlot={rightSlot} rightSlotClassName={rightSlotClassName} isOpen={isOpen} />
                </button>
            )}
        </AstraTooltip>
    )
}
